import { DATETIME_FORMAT } from '@/app/const';
import { CopyIcon } from '@/components/Icons';
import { CourseLibraryPermissions } from '@/lib/auth/permissions/courseLibraryPermissions.enum';
import { useUserHasCapabilities } from '@/lib/auth/useUserHasCapabilities';
import { Translations } from '@/lib/translation';
import { getQueryString, pushParamsToUrl, sanitizeHTML } from '@/lib/util';
import { actions as flashMessageActions } from '@/modules/reducers/flashMessage';
import {
  useDownloadLibraryV2Mutation,
  useGetVideoLibraryElearningV2Query,
} from '@/modules/services';
import { CourseLibraryDefaultQueryString } from '@/pages/CourseLibrary/CourseLibraryRoutes';
import { format as formatDate } from 'date-fns';
import {
  ListTable,
  MenuIcon,
  Modal,
  RemoveRedEyeOutlinedIcon,
  Spinner,
  Text,
} from 'elmo-elements';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import './index.scss';
import { CourseLibraryViewData } from '@/pages/CourseLibrary/types';
import { appData } from '@/lib/data';

const tableColumns = {
  name: { label: 'Name' },
  category: { label: 'Category' },
  publishedDate: { label: 'Last Published' },
};

const sortableColumns = [
  { key: 'name', name: 'Name' },
  { key: 'category_id', name: 'Category' },
  { key: 'published_at', name: 'Last Published' },
];

export const enum ModalContext {
  COPY_COURSE_IN_PROGRESS = 'CopyCourseInProgress',
  LEARN_MORE = 'LearnMore',
}

const VideoLibraryListV2 = (): React.ReactElement => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [modalContent, setModalContent] = useState<TrustedHTML>();
  const [isDownloadModalOpen, setIsDownloadModalOpen] =
    useState<boolean>(false);

  const canDownload = useUserHasCapabilities([
    CourseLibraryPermissions.DOWNLOAD_COURSE_LIBRARY,
  ]);

  const pageSize = 10;
  const currentQueryString = getQueryString(
    location.search,
    CourseLibraryDefaultQueryString,
  );

  const { data: videoLibraryData, isLoading } =
    useGetVideoLibraryElearningV2Query(currentQueryString);

  const [downloadLibrary] = useDownloadLibraryV2Mutation();

  const handleSort = (column: number, direction: string) => {
    pushParamsToUrl(
      {
        sort: sortableColumns[column].key,
        order: direction.toUpperCase(),
        page: 1,
      },
      history,
    );
  };

  const handleDownloadCourse = async (courseId: string) => {
    try {
      setIsDownloadModalOpen(true);
      await downloadLibrary(courseId).unwrap();

      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Course successfully copied to My Library!`,
        },
      });
    } catch (error) {
      dispatch({
        type: flashMessageActions.addFlashMessage,
        payload: {
          isActive: true,
          message: `Failed to copy course to My Library. Please try again.`,
        },
      });
    } finally {
      setIsDownloadModalOpen(false);
    }
  };

  const handlePreviewCourse = (elearning: CourseLibraryViewData) => {
    const { windowMetadata } = appData.playerTheme.default;

    if (windowMetadata) {
      window.PREVIEW_WINDOW = window.open(
        `/preview-live/${elearning.tenantId}/${elearning.id}/live`,
        'preview',
        `width=${windowMetadata.width + 5},height=${
          windowMetadata.height + 10
        }, location=0,status=0, scrollbars=0, toolbar=0,menubar=0,resizable=1`,
      );
    }
  };

  const handleLearnMoreClick = (elearningInfo = '') => {
    setIsModalOpen(true);
    setModalContent(sanitizeHTML(elearningInfo));
  };

  if (!videoLibraryData || isLoading) {
    return <Spinner />;
  }
  return (
    <>
      <Modal
        id="DownloadProgress"
        type="medium"
        isOpened={isDownloadModalOpen}
        closeModal={() => setIsDownloadModalOpen(false)}
        title={Translations.courseLibrary.copyCourse.inProgressTitle.default}
      >
        <div className="publish-in-progress-container">
          <div className="publish-in-progress-container__spinner">
            <Spinner size="xl" />
          </div>
          <p>{Translations.courseLibrary.copyCourse.inProgressInfo.default}</p>
        </div>
      </Modal>
      <Modal
        id="LearnMore"
        type="medium"
        isOpened={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        title="About this module"
      >
        <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(modalContent) }} />
      </Modal>
      <ListTable
        id="CourseLibraryTable"
        ariaLabel="List of Course Library Modules"
        onSort={handleSort}
      >
        <ListTable.Header>
          <ListTable.Column label={tableColumns.name.label} sortable={true} />
          <ListTable.Column
            label={tableColumns.category.label}
            sortable={false}
          />
          <ListTable.Column
            label={tableColumns.publishedDate.label}
            sortable={true}
          />
        </ListTable.Header>
        <ListTable.Body>
          {videoLibraryData.data.map(
            (elearning: CourseLibraryViewData, index: number) => (
              <ListTable.Tr
                data-testid="list-table-item"
                key={elearning.id}
                actions={
                  canDownload && [
                    {
                      icon: <RemoveRedEyeOutlinedIcon />,
                      label: 'Preview',
                      onClick: () => {
                        handlePreviewCourse(elearning);
                      },
                    },
                    {
                      icon: <CopyIcon />,
                      label: 'Copy to My Library',
                      onClick: () => {
                        handleDownloadCourse(elearning.id);
                      },
                      withDivider: true,
                    },
                    {
                      icon: <MenuIcon />,
                      label: 'Learn more',
                      onClick: () => {
                        handleLearnMoreClick(elearning.description);
                      },
                    },
                  ]
                }
              >
                <ListTable.Td>{elearning.name}</ListTable.Td>
                <ListTable.Td>
                  <div className="course-library-container">
                    {elearning.categoryNames.map((categoryName) => (
                      <span
                        className="course-library-item"
                        key={categoryName}
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(categoryName),
                        }}
                      />
                    ))}
                  </div>
                </ListTable.Td>
                <ListTable.Td>
                  <div className="course-library-container">
                    <span className="course-library-item">
                      {elearning.publishedToLiveAt ? (
                        formatDate(
                          new Date(elearning.publishedToLiveAt),
                          DATETIME_FORMAT,
                        )
                      ) : (
                        <>-</>
                      )}
                    </span>
                    <span className="course-library-item">
                      <Text isBold={true} size="sm">
                        <b>Last downloaded </b>
                        {elearning.downloadedAt ? (
                          formatDate(
                            new Date(elearning.downloadedAt),
                            DATETIME_FORMAT,
                          )
                        ) : (
                          <>-</>
                        )}
                      </Text>
                    </span>
                  </div>
                </ListTable.Td>
              </ListTable.Tr>
            ),
          )}
        </ListTable.Body>
        <ListTable.Pagination
          hideDropdown={true}
          pageSize={pageSize}
          currentPage={currentQueryString?.page || 1}
          onPageChange={(pageNumber) => {
            /* This is intentional */
            pushParamsToUrl({ page: pageNumber }, history);
          }}
          onPageSizeChange={() => {}}
          totalResults={videoLibraryData?.meta.itemCount || 0}
        />
      </ListTable>
    </>
  );
};

export default VideoLibraryListV2;
